import React, { useState, useEffect } from 'react';
import { Header, Segment, Loader, Message, List, Divider } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import apiClient from '../Utils/ApiConfig';

interface EventRegistrant {
    id: number;
    user_id: number;
    first_name: string;
    last_name: string;
    pledge_class: string;
    chapter: string;
    year: number;
    order_number: string;
    order_email: string;
    date_created: string;
}

interface EventRegistrantsResponse {
    registrants: EventRegistrant[];
    total_count: number;
}

interface EventRegistrantsProps {
    formId: number;
    eventName?: string;
    attendeeTerm?: string; // e.g., "attendee", "registrant", "RSVP" (singular form)
}

export default function EventRegistrants({ formId, eventName = "Event", attendeeTerm = "registrant" }: EventRegistrantsProps) {
    const [registrants, setRegistrants] = useState<EventRegistrantsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Helper functions for text formatting
    const capitalize = (str: string): string => {
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const pluralize = (term: string): string => {
        // Handle special cases
        if (term.toLowerCase() === 'rsvp') return 'RSVPs';
        if (term.toLowerCase() === 'person') return 'people';
        if (term.toLowerCase() === 'child') return 'children';

        // Default pluralization: add 's'
        return term + 's';
    };

    const getPluralTerm = (): string => pluralize(attendeeTerm);
    const getCapitalizedPluralTerm = (): string => capitalize(pluralize(attendeeTerm));

    useEffect(() => {
        const fetchRegistrants = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await apiClient.get(`/events/${formId}/registrants`);

                setRegistrants(response.data);
            } catch (err: any) {
                setError(err.response?.data?.message || err.message || 'An error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchRegistrants();
    }, [formId]);

    if (loading) {
        return (
            <Segment>
                <Loader active inline="centered">
                    Loading {getPluralTerm()}...
                </Loader>
            </Segment>
        );
    }

    if (error) {
        return (
            <Message negative>
                <Message.Header>Error Loading {getCapitalizedPluralTerm()}</Message.Header>
                <p>{error}</p>
            </Message>
        );
    }

    if (!registrants || registrants.total_count === 0) {
        return (
            <Message info>
                <Message.Header>No {getCapitalizedPluralTerm()} Found</Message.Header>
                <p>No completed {getPluralTerm()} found for this event.</p>
            </Message>
        );
    }

    // Group registrants by pledge class, maintaining the order from backend (sorted by graduation year)
    const groupedRegistrants = registrants.registrants.reduce((groups, registrant) => {
        const pledgeClass = registrant.pledge_class || 'Unknown';
        if (!groups[pledgeClass]) {
            groups[pledgeClass] = [];
        }
        groups[pledgeClass].push(registrant);
        return groups;
    }, {} as { [pledgeClass: string]: EventRegistrant[] });

    // Get unique pledge classes in the order they appear (which should be by graduation year from backend)
    const seenPledgeClasses = new Set<string>();
    const sortedPledgeClasses: string[] = [];

    for (const registrant of registrants.registrants) {
        const pledgeClass = registrant.pledge_class || 'Unknown';
        if (!seenPledgeClasses.has(pledgeClass)) {
            seenPledgeClasses.add(pledgeClass);
            sortedPledgeClasses.push(pledgeClass);
        }
    }

    return (
        <Segment>
            <Header as="h3">
                {eventName} {getCapitalizedPluralTerm()}
                <Header.Subheader>
                    {registrants.total_count} {registrants.total_count !== 1 ? getPluralTerm() : attendeeTerm}
                </Header.Subheader>
            </Header>

            {sortedPledgeClasses.map((pledgeClass, index) => {
                const classRegistrants = groupedRegistrants[pledgeClass];

                return (
                    <div key={pledgeClass}>
                        {index > 0 && <Divider />}
                        <Header as="h4" color="blue">
                            {pledgeClass}
                        </Header>
                        <List relaxed>
                            {classRegistrants.map((registrant) => (
                                <List.Item key={registrant.id} style={{ paddingLeft: '20px' }}>
                                    <List.Content>
                                        <List.Header>
                                            <Link to={`/users/${registrant.user_id}`}>
                                                {registrant.first_name} {registrant.last_name}
                                            </Link>
                                            , {registrant.chapter} {registrant.year}
                                        </List.Header>
                                    </List.Content>
                                </List.Item>
                            ))}
                        </List>
                    </div>
                );
            })}
        </Segment>
    );
}
